import { z } from 'zod';
import type { HttpClient } from './http';

export const paperlessTagSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string().optional(),
  colour: z.number().optional(),
});
export type PaperlessTag = z.infer<typeof paperlessTagSchema>;

export const paperlessCorrespondentSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string().optional(),
});
export type PaperlessCorrespondent = z.infer<typeof paperlessCorrespondentSchema>;

export const paperlessDocumentTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string().optional(),
});
export type PaperlessDocumentType = z.infer<typeof paperlessDocumentTypeSchema>;

export const paperlessDocumentSchema = z.object({
  id: z.number(),
  title: z.string(),
  content: z.string().optional(),
  tags: z.array(z.number()),
  correspondent: z.number().nullable(),
  document_type: z.number().nullable(),
  created: z.string(),
  modified: z.string(),
  added: z.string(),
  archive_serial_number: z.number().nullable().optional(),
  original_file_name: z.string().optional(),
});
export type PaperlessDocument = z.infer<typeof paperlessDocumentSchema>;

export const paperlessDocumentListSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(paperlessDocumentSchema),
});
export type PaperlessDocumentList = z.infer<typeof paperlessDocumentListSchema>;

export const paperlessTagListSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(paperlessTagSchema),
});
export type PaperlessTagList = z.infer<typeof paperlessTagListSchema>;

export const paperlessCorrespondentListSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(paperlessCorrespondentSchema),
});
export type PaperlessCorrespondentList = z.infer<typeof paperlessCorrespondentListSchema>;

export const paperlessDocumentTypeListSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(paperlessDocumentTypeSchema),
});
export type PaperlessDocumentTypeList = z.infer<typeof paperlessDocumentTypeListSchema>;

export interface ListDocumentsParams {
  query?: string;
  tags?: number[];
  correspondent?: number;
  documentType?: number;
  page?: number;
  pageSize?: number;
  ordering?: string;
}

export interface UpdateDocumentPatch {
  title?: string;
  correspondent?: number | null;
  documentType?: number | null;
  tags?: number[];
}

export interface UploadDocumentMeta {
  title?: string;
  created?: string;
  correspondent?: number;
  documentType?: number;
  tags?: number[];
}

export interface UploadDocumentResponse {
  taskId: string;
}

export const uploadDocumentResponseSchema = z.object({
  taskId: z.string(),
});

export class DocumentsResource {
  constructor(private readonly http: HttpClient) {}

  list(params: ListDocumentsParams = {}, signal?: AbortSignal): Promise<PaperlessDocumentList> {
    const search = new URLSearchParams();
    if (params.query) search.set('query', params.query);
    if (params.tags?.length) search.set('tags', params.tags.join(','));
    if (params.correspondent !== undefined) search.set('correspondent', String(params.correspondent));
    if (params.documentType !== undefined) search.set('documentType', String(params.documentType));
    if (params.page !== undefined) search.set('page', String(params.page));
    if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
    if (params.ordering) search.set('ordering', params.ordering);
    const qs = search.toString();
    return this.http.request({
      path: `/documents${qs ? `?${qs}` : ''}`,
      responseSchema: paperlessDocumentListSchema,
      signal,
    });
  }

  getOne(id: number, signal?: AbortSignal): Promise<PaperlessDocument> {
    return this.http.request({
      path: `/documents/${id}`,
      responseSchema: paperlessDocumentSchema,
      signal,
    });
  }

  listTags(signal?: AbortSignal): Promise<PaperlessTagList> {
    return this.http.request({
      path: '/documents/tags',
      responseSchema: paperlessTagListSchema,
      signal,
    });
  }

  listCorrespondents(signal?: AbortSignal): Promise<PaperlessCorrespondentList> {
    return this.http.request({
      path: '/documents/correspondents',
      responseSchema: paperlessCorrespondentListSchema,
      signal,
    });
  }

  listDocumentTypes(signal?: AbortSignal): Promise<PaperlessDocumentTypeList> {
    return this.http.request({
      path: '/documents/document-types',
      responseSchema: paperlessDocumentTypeListSchema,
      signal,
    });
  }

  upload(
    file: Blob | { uri: string; name: string; type: string },
    meta: UploadDocumentMeta = {},
    signal?: AbortSignal,
  ): Promise<UploadDocumentResponse> {
    const form = new FormData();
    form.append('document', file as unknown as Blob);
    if (meta.title !== undefined) form.append('title', meta.title);
    if (meta.created !== undefined) form.append('created', meta.created);
    if (meta.correspondent !== undefined) form.append('correspondent', String(meta.correspondent));
    if (meta.documentType !== undefined) form.append('documentType', String(meta.documentType));
    if (meta.tags?.length) form.append('tags', meta.tags.join(','));
    return this.http.request({
      method: 'POST',
      path: '/documents',
      formData: form,
      responseSchema: uploadDocumentResponseSchema,
      signal,
    });
  }

  update(id: number, patch: UpdateDocumentPatch, signal?: AbortSignal): Promise<PaperlessDocument> {
    return this.http.request({
      method: 'PATCH',
      path: `/documents/${id}`,
      body: patch,
      responseSchema: paperlessDocumentSchema,
      signal,
    });
  }

  previewBlob(id: number, signal?: AbortSignal): Promise<Blob> {
    return this.http.requestBlob(`/documents/${id}/preview`, signal);
  }

  downloadBlob(id: number, signal?: AbortSignal): Promise<Blob> {
    return this.http.requestBlob(`/documents/${id}/download`, signal);
  }

  previewUrl(id: number, baseUrl: string): string {
    return `${baseUrl.replace(/\/$/, '')}/documents/${id}/preview`;
  }

  thumbnailUrl(id: number, baseUrl: string): string {
    return `${baseUrl.replace(/\/$/, '')}/documents/${id}/thumbnail`;
  }
}
